import { useTranslation } from "react-i18next";
import {
  Clock, Server, Cloud, GitBranch, Phone, Plug, CheckCircle, Shield, Building, UserCheck,
  TrendingUp, Users, Target, Zap, Eye, Activity, Lock, Briefcase, Package, Calendar,
  Smile, BadgeCheck, GraduationCap, Stethoscope, Landmark, ShieldCheck, Radar,
  Database, Bell, BarChart3, Network, Monitor, CloudOff, Layers,
  AlertTriangle, UserX, KeyRound, FileDown, Building2, Factory, Truck, HeartPulse,
  ShoppingCart, Scale, Brain, ShieldAlert, Bug, Upload, Search, Sword,
  FileText, Bot, Crosshair, Wrench, Smartphone,
  Award, FlaskConical, Timer, Boxes, Laptop, ShieldX, Fingerprint, Ban,
  Cpu, Layers3, Terminal, Globe2, History
} from "lucide-react";

// Feature icon mapping (preserved)
const featureIcons = {
  // Skydial features
  onpremise: Server,
  cloud: Cloud,
  hybrid: GitBranch,
  branch: Building,
  sip: Phone,

  // Voice API features
  integration: Plug,
  scalable: TrendingUp,
  reliable: Shield,

  // RFID features
  supply: TrendingUp,
  asset: Package,
  event: Calendar,

  // Networking features
  bandwidth: Zap,
  latency: Activity,
  rural: Building,

  // AI Counting - Benefits
  traffic: TrendingUp,
  optimize: Target,
  marketing: Users,
  experience: Smile,
  efficiency: Zap,

  // AI Counting - Analytics
  age: UserCheck,
  gender: Users,
  employee: BadgeCheck,
  reid: Eye,
  accuracy: Target,

  // AI Counting - Behavioral
  dwell: Clock,
  flow: Activity,
  anonymous: Shield,
  realtime: Clock,

  // VMS - Main Features
  efficient: Zap,
  professional: Briefcase,
  compliance: Shield,
  secure: Lock,

  // VMS - Sectors
  corporate: Building,
  government: Landmark,
  hospital: Stethoscope,
  education: GraduationCap,

  // SOC - Main Features
  visibility: Eye,
  monitoring: Radar,
  ai: Brain,
  soccompliance: CheckCircle,
  sovereignty: CloudOff,
  multitenancy: Layers,

  // SOC - Architecture
  collection: Database,
  normalization: Layers,
  correlation: Zap,
  storage: Server,
  alerting: Bell,
  reporting: BarChart3,

  // SOC - Log Sources
  network: Network,
  os: Monitor,
  identity: KeyRound,
  soccloud: Cloud,
  endpoint: ShieldCheck,
  applications: Briefcase,

  // SOC - Use Cases
  abnormal: AlertTriangle,
  privilege: KeyRound,
  compromised: UserX,
  ransomware: ShieldAlert,
  insider: UserX,
  massdownload: FileDown,
  multisite: Building2,

  // SOC - Industries
  manufacturing: Factory,
  logistics: Truck,
  healthcare: HeartPulse,
  financial: Landmark,
  retail: ShoppingCart,
  socprofessional: Scale,

  // VAPT - Main Features
  autonomous: Bot,
  agentic: Boxes,
  onprem: Server,
  supervised: UserCheck,
  boxmodes: Lock,
  vaptreporting: FileText,

  // VAPT - Stages
  upload: Upload,
  assessment: Search,
  pentest: Sword,
  report: FileText,

  // VAPT - Agents
  orchestrator: Brain,
  recon: Radar,
  scanner: Crosshair,
  exploit: FlaskConical,
  vaptreporter: FileText,

  // VAPT - Coverage
  systemva: Monitor,
  mobile: Smartphone,
  api: Plug,
  server: Server,
  appvapt: Bug,
  ad: KeyRound,

  // VAPT - Testing Modes
  blackbox: Eye,
  greybox: Users,
  whitebox: ShieldCheck,

  // VAPT - Impact
  discovery: Search,
  turnaround: Timer,
  toolchain: Wrench,
  reporttime: Clock,

  // VAPT - Trust
  humanloop: UserCheck,
  precision: Target,
  pilot: Award,

  // FirstProtect - Main Features
  fpprevention: ShieldX,
  fpondevice: Brain,
  fpsigma: Layers3,
  fpcrossplatform: Laptop,
  fpuserspace: Cpu,
  fpmodules: Boxes,

  // FirstProtect - AgentProtect
  fpidentity: Fingerprint,
  fpenforcement: Ban,
  fpforensics: History,

  // FirstProtect - Protect Modules
  fpdata: Database,
  fpshell: Terminal,
  fpcredential: KeyRound,
  fpapp: Globe2,
  fproot: Server,
  fpdestructive: ShieldAlert,

  // FirstProtect - Deployment
  fpzerotouch: Upload,
  fpdataplane: Lock,
  fpauditmode: Eye,
  fpoffline: CloudOff,
  fpsoc: Radar,
  fpalertquality: CheckCircle,

  // FirstProtect - Proof
  fpexfil: ShieldCheck,
  fpofflineproof: CloudOff,
  fpearlier: Timer,

  // FirstProtect - Stats
  fplatency: Zap,
  fpefficacy: Target,
  fpcpu: Cpu,
  fpplatforms: Laptop,
};

const SolutionFeatures = ({ solution }) => {
  const { t } = useTranslation();

  const getFeatureIcon = (featureKey) => {
    return featureIcons[featureKey] || CheckCircle;
  };

  /* ---------- Shared building blocks (Vercel structure, brand palette) ---------- */

  // Section heading
  const SectionHeading = ({ children, subtitle }) => (
    <div className="mb-10 max-w-3xl">
      <h2 className="text-3xl tablet:text-2xl mobile:text-xl font-semibold tracking-[-0.02em] text-black">{children}</h2>
      {subtitle && <p className="mt-3 text-sm text-gray-600 leading-relaxed">{subtitle}</p>}
    </div>
  );

  // Text-only feature card (icon + single paragraph)
  const TextCard = ({ feature, color = "text-textBlue" }) => {
    const FeatureIcon = getFeatureIcon(feature);
    return (
      <div className="group flex items-start gap-4 rounded-xl border border-line bg-white p-6 transition-[border-color,box-shadow,transform] duration-300 hover:border-textBlue/40 hover:shadow-lift hover:-translate-y-1">
        <span className="shrink-0 transition-transform duration-300 group-hover:scale-110">
          <FeatureIcon className={`w-8 h-8 ${color}`} aria-hidden="true" />
        </span>
        <p className="min-w-0 text-sm text-gray-700 leading-relaxed break-words">
          {t(`solutions.${solution.key}.features.${feature}`)}
        </p>
      </div>
    );
  };

  // Titled feature card (icon + title + description)
  const TitledCard = ({ feature, color = "text-textBlue", path }) => {
    const FeatureIcon = getFeatureIcon(feature);
    return (
      <div className="group flex items-start gap-4 rounded-xl border border-line bg-white p-6 transition-[border-color,box-shadow,transform] duration-300 hover:border-textBlue/40 hover:shadow-lift hover:-translate-y-1">
        <span className="shrink-0 transition-transform duration-300 group-hover:scale-110">
          <FeatureIcon className={`w-8 h-8 ${color}`} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="font-semibold text-black text-base mb-1 break-words">
            {t(`solutions.${solution.key}.${path}.${feature}.title`)}
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed break-words">
            {t(`solutions.${solution.key}.${path}.${feature}.description`)}
          </p>
        </div>
      </div>
    );
  };

  // Numbered card (centered, for process/stage flows)
  const NumberedCard = ({ feature, idx, color = "text-textBlue", path }) => {
    const FeatureIcon = getFeatureIcon(feature);
    return (
      <div className="group flex flex-col items-center text-center rounded-xl border border-line bg-white p-6 transition-[border-color,box-shadow,transform] duration-300 hover:border-textBlue/40 hover:shadow-lift hover:-translate-y-1">
        <div className="w-14 h-14 rounded-full bg-brand-50 flex items-center justify-center mb-3 transition-transform duration-300 group-hover:scale-110">
          <FeatureIcon className={`w-7 h-7 ${color}`} aria-hidden="true" />
        </div>
        <div className="text-xs font-bold text-textBlue uppercase tracking-wider mb-1">
          {String(idx + 1).padStart(2, "0")}
        </div>
        <h3 className="font-semibold text-black text-sm mb-1">
          {t(`solutions.${solution.key}.${path}.${feature}.title`)}
        </h3>
        <p className="text-gray-500 text-xs leading-relaxed">
          {t(`solutions.${solution.key}.${path}.${feature}.description`)}
        </p>
      </div>
    );
  };

  /* ---------------------------- Renderers ---------------------------- */

  const renderStandardFeatures = () => (
    <>
      <SectionHeading>Key Features</SectionHeading>
      <div className="grid grid-cols-2 tablet:grid-cols-1 gap-6">
        {solution.features.map((feature, idx) => (
          <TextCard key={idx} feature={feature} color={solution.color} />
        ))}
      </div>
    </>
  );

  const renderVMSFeatures = () => (
    <>
      <SectionHeading>Key Features</SectionHeading>
      <div className="grid grid-cols-2 tablet:grid-cols-1 gap-6 mb-20">
        {solution.features.main.map((feature, idx) => (
          <TitledCard key={idx} feature={feature} path="features" color="text-emerald-600" />
        ))}
      </div>

      <SectionHeading>{t(`solutions.${solution.key}.sectors.title`)}</SectionHeading>
      <div className="grid grid-cols-2 tablet:grid-cols-1 gap-6">
        {solution.features.sectors.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="group flex items-start gap-4 rounded-xl border border-line bg-white p-6 transition-[border-color,box-shadow,transform] duration-300 hover:border-textBlue/40 hover:shadow-lift hover:-translate-y-1"
            >
              <span className="shrink-0 transition-transform duration-300 group-hover:scale-110">
                <FeatureIcon className="w-8 h-8 text-teal-600" aria-hidden="true" />
              </span>
              <p className="min-w-0 text-base font-semibold text-black break-words">
                {t(`solutions.${solution.key}.sectors.${feature}`)}
              </p>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderFirstProtectFeatures = () => (
    <>
      <SectionHeading>{t(`solutions.${solution.key}.stats.title`)}</SectionHeading>
      <div className="grid grid-cols-4 tablet:grid-cols-2 gap-6 mb-20">
        {solution.features.stats.map((feature, idx) => (
          <div
            key={idx}
            className="bg-white p-6 rounded-xl border border-line text-center transition-[border-color,box-shadow] duration-300 hover:shadow-lift hover:border-textBlue/40"
          >
            <h3 className="text-3xl tablet:text-2xl font-semibold text-indigo-600 mb-1">
              {t(`solutions.${solution.key}.stats.${feature}.title`)}
            </h3>
            <p className="text-gray-600 text-sm">
              {t(`solutions.${solution.key}.stats.${feature}.description`)}
            </p>
          </div>
        ))}
      </div>

      <SectionHeading>Key Features</SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-1 gap-6 mb-20">
        {solution.features.main.map((feature, idx) => (
          <TitledCard key={idx} feature={feature} path="features" color="text-indigo-600" />
        ))}
      </div>

      <SectionHeading subtitle={t(`solutions.${solution.key}.agentprotect.subtitle`)}>
        {t(`solutions.${solution.key}.agentprotect.title`)}
      </SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-1 gap-6 mb-20">
        {solution.features.agentprotect.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-6 rounded-xl border border-line transition-[border-color,box-shadow,transform] duration-300 hover:shadow-lift hover:-translate-y-1 border-t-4 border-t-indigo-500"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mb-3">
                  <FeatureIcon className="w-7 h-7 text-indigo-600" aria-hidden="true" />
                </div>
                <div className="text-xs font-bold text-indigo-700 uppercase tracking-wider mb-1">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <h3 className="font-semibold text-black text-sm mb-1">
                  {t(`solutions.${solution.key}.agentprotect.${feature}.title`)}
                </h3>
                <p className="text-gray-500 text-xs leading-relaxed">
                  {t(`solutions.${solution.key}.agentprotect.${feature}.description`)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <SectionHeading subtitle={t(`solutions.${solution.key}.protectmodules.subtitle`)}>
        {t(`solutions.${solution.key}.protectmodules.title`)}
      </SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-1 gap-6 mb-20">
        {solution.features.protectmodules.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-5 rounded-xl border border-line transition-[border-color,box-shadow] duration-300 hover:shadow-lift hover:border-textBlue/40"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                  <FeatureIcon className="w-5 h-5 text-indigo-600" aria-hidden="true" />
                </div>
                <h3 className="font-semibold text-black text-sm">
                  {t(`solutions.${solution.key}.protectmodules.${feature}.title`)}
                </h3>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed pl-13">
                {t(`solutions.${solution.key}.protectmodules.${feature}.description`)}
              </p>
            </div>
          );
        })}
      </div>

      <SectionHeading subtitle={t(`solutions.${solution.key}.deployment.subtitle`)}>
        {t(`solutions.${solution.key}.deployment.title`)}
      </SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-1 gap-6 mb-20">
        {solution.features.deployment.map((feature, idx) => (
          <TitledCard key={idx} feature={feature} path="deployment" color="text-textBlue" />
        ))}
      </div>

      <SectionHeading subtitle={t(`solutions.${solution.key}.proof.subtitle`)}>
        {t(`solutions.${solution.key}.proof.title`)}
      </SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-1 gap-6">
        {solution.features.proof.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-6 rounded-xl border border-line transition-[border-color,box-shadow] duration-300 hover:shadow-lift hover:border-textBlue/40"
            >
              <div className="flex flex-col items-center text-center">
                <FeatureIcon className="w-10 h-10 text-emerald-600 mb-3" aria-hidden="true" />
                <h3 className="font-semibold text-black text-sm mb-2">
                  {t(`solutions.${solution.key}.proof.${feature}.title`)}
                </h3>
                <p className="text-gray-500 text-xs leading-relaxed italic">
                  {t(`solutions.${solution.key}.proof.${feature}.description`)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderVAPTFeatures = () => (
    <>
      <SectionHeading>Key Features</SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-1 gap-6 mb-20">
        {solution.features.main.map((feature, idx) => (
          <TitledCard key={idx} feature={feature} path="features" color="text-amber-600" />
        ))}
      </div>

      <SectionHeading subtitle={t(`solutions.${solution.key}.stages.subtitle`)}>
        {t(`solutions.${solution.key}.stages.title`)}
      </SectionHeading>
      <div className="grid grid-cols-4 tablet:grid-cols-1 gap-6 mb-20">
        {solution.features.stages.map((feature, idx) => (
          <NumberedCard key={idx} feature={feature} idx={idx} path="stages" color="text-amber-600" />
        ))}
      </div>

      <SectionHeading subtitle={t(`solutions.${solution.key}.agents.subtitle`)}>
        {t(`solutions.${solution.key}.agents.title`)}
      </SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-1 gap-6 mb-20">
        {solution.features.agents.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-5 rounded-xl border border-line transition-[border-color,box-shadow] duration-300 hover:shadow-lift hover:border-textBlue/40"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                  <FeatureIcon className="w-5 h-5 text-amber-600" aria-hidden="true" />
                </div>
                <h3 className="font-semibold text-black text-sm">
                  {t(`solutions.${solution.key}.agents.${feature}.title`)}
                </h3>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed pl-13">
                {t(`solutions.${solution.key}.agents.${feature}.description`)}
              </p>
            </div>
          );
        })}
      </div>

      <SectionHeading subtitle={t(`solutions.${solution.key}.coverage.subtitle`)}>
        {t(`solutions.${solution.key}.coverage.title`)}
      </SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-1 gap-6 mb-20">
        {solution.features.coverage.map((feature, idx) => (
          <TitledCard key={idx} feature={feature} path="coverage" color="text-textBlue" />
        ))}
      </div>

      <SectionHeading subtitle={t(`solutions.${solution.key}.testingmodes.subtitle`)}>
        {t(`solutions.${solution.key}.testingmodes.title`)}
      </SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-1 gap-6 mb-20">
        {solution.features.testingmodes.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-6 rounded-xl border border-line transition-[border-color,box-shadow,transform] duration-300 hover:shadow-lift hover:-translate-y-1 border-t-4 border-t-amber-500"
            >
              <div className="flex flex-col items-center text-center">
                <FeatureIcon className="w-10 h-10 text-amber-600 mb-3" aria-hidden="true" />
                <h3 className="font-semibold text-black text-base mb-2">
                  {t(`solutions.${solution.key}.testingmodes.${feature}.title`)}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  {t(`solutions.${solution.key}.testingmodes.${feature}.description`)}
                </p>
                <span className="inline-block bg-amber-50 text-amber-700 text-xs font-medium px-3 py-1 rounded-full">
                  {t(`solutions.${solution.key}.testingmodes.${feature}.knows`)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <SectionHeading subtitle={t(`solutions.${solution.key}.impact.subtitle`)}>
        {t(`solutions.${solution.key}.impact.title`)}
      </SectionHeading>
      <div className="grid grid-cols-4 tablet:grid-cols-2 gap-6 mb-20">
        {solution.features.impact.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-6 rounded-xl border border-line transition-[border-color,box-shadow] duration-300 hover:shadow-lift hover:border-textBlue/40 text-center"
            >
              <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
                <FeatureIcon className="w-6 h-6 text-amber-600" aria-hidden="true" />
              </div>
              <h3 className="font-semibold text-black text-sm mb-1">
                {t(`solutions.${solution.key}.impact.${feature}.title`)}
              </h3>
              <p className="text-gray-500 text-xs leading-relaxed">
                {t(`solutions.${solution.key}.impact.${feature}.description`)}
              </p>
            </div>
          );
        })}
      </div>

      <SectionHeading subtitle={t(`solutions.${solution.key}.trust.subtitle`)}>
        {t(`solutions.${solution.key}.trust.title`)}
      </SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-1 gap-6">
        {solution.features.trust.map((feature, idx) => (
          <TitledCard key={idx} feature={feature} path="trust" color="text-amber-600" />
        ))}
      </div>
    </>
  );

  const renderSOCFeatures = () => (
    <>
      <SectionHeading>Key Features</SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-1 gap-6 mb-20">
        {solution.features.main.map((feature, idx) => (
          <TitledCard key={idx} feature={feature} path="features" color="text-red-600" />
        ))}
      </div>

      <SectionHeading subtitle={t(`solutions.${solution.key}.architecture.subtitle`)}>
        {t(`solutions.${solution.key}.architecture.title`)}
      </SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-1 gap-6 mb-20">
        {solution.features.architecture.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-6 rounded-xl border border-line transition-[border-color,box-shadow,transform] duration-300 hover:shadow-lift hover:-translate-y-1 group"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-3 transition-transform duration-300 group-hover:scale-110">
                  <FeatureIcon className="w-7 h-7 text-red-600" aria-hidden="true" />
                </div>
                <h3 className="font-semibold text-black text-sm mb-1">
                  {t(`solutions.${solution.key}.architecture.${feature}.title`)}
                </h3>
                <p className="text-gray-500 text-xs leading-relaxed">
                  {t(`solutions.${solution.key}.architecture.${feature}.description`)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <SectionHeading>{t(`solutions.${solution.key}.logsources.title`)}</SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-1 gap-6 mb-20">
        {solution.features.logsources.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-5 rounded-xl border border-line transition-[border-color,box-shadow] duration-300 hover:shadow-lift hover:border-textBlue/40"
            >
              <div className="flex items-center gap-3 mb-2">
                <FeatureIcon className="w-6 h-6 text-textBlue shrink-0" aria-hidden="true" />
                <h3 className="font-semibold text-black text-sm">
                  {t(`solutions.${solution.key}.logsources.${feature}.title`)}
                </h3>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed pl-9">
                {t(`solutions.${solution.key}.logsources.${feature}.items`)}
              </p>
            </div>
          );
        })}
      </div>

      <SectionHeading>{t(`solutions.${solution.key}.usecases.title`)}</SectionHeading>
      <div className="grid grid-cols-2 tablet:grid-cols-1 gap-6 mb-20">
        {solution.features.usecases.map((feature, idx) => (
          <TitledCard key={idx} feature={feature} path="usecases" color="text-orange-600" />
        ))}
      </div>

      <SectionHeading>{t(`solutions.${solution.key}.industries.title`)}</SectionHeading>
      <div className="grid grid-cols-3 tablet:grid-cols-1 gap-6">
        {solution.features.industries.map((feature, idx) => {
          const FeatureIcon = getFeatureIcon(feature);
          return (
            <div
              key={idx}
              className="bg-white p-5 rounded-xl border border-line transition-[border-color,box-shadow] duration-300 hover:shadow-lift hover:border-textBlue/40"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                  <FeatureIcon className="w-5 h-5 text-red-600" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-black text-sm mb-1">
                    {t(`solutions.${solution.key}.industries.${feature}.title`)}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed break-words">
                    {t(`solutions.${solution.key}.industries.${feature}.description`)}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderAICountingFeatures = () => (
    <>
      <SectionHeading>Key Benefits</SectionHeading>
      <div className="grid grid-cols-2 tablet:grid-cols-1 gap-6 mb-20">
        {solution.features.benefits.map((feature, idx) => (
          <TextCard key={idx} feature={feature} color="text-textBlue" />
        ))}
      </div>

      <SectionHeading>{t(`solutions.${solution.key}.analytics`)}</SectionHeading>
      <div className="grid grid-cols-2 tablet:grid-cols-1 gap-6 mb-20">
        {solution.features.analytics.map((feature, idx) => (
          <TextCard key={idx} feature={feature} color="text-purple-600" />
        ))}
      </div>

      <SectionHeading>{t(`solutions.${solution.key}.behavioral`)}</SectionHeading>
      <div className="grid grid-cols-2 tablet:grid-cols-1 gap-6">
        {solution.features.behavioral.map((feature, idx) => (
          <TextCard key={idx} feature={feature} color="text-indigo-600" />
        ))}
      </div>
    </>
  );

  const renderSkydialFeatures = () => {
    const benefitIcons = {
      costEffective: "/skydial-cost-effective.svg",
      flexible: "/skydial-flexible.svg",
      scalable: "/skydial-scalable.svg",
      updates: "/skydial-updates.svg",
      integration: "/skydial-integration.svg",
    };
    const featureIconsMap = {
      ivr: "/skydial-ivr.svg",
      voicemail: "/skydial-voicemail.svg",
      forwarding: "/skydial-forwarding.svg",
      conference: "/skydial-conference.svg",
      management: "/skydial-management.svg",
    };
    const whyIcons = {
      security: "/skydial-cyber-security.svg",
      easy: "/skydial-interactive.svg",
      modern: "/skydial-modernization.svg",
      support: "/skydial-customer-support.svg",
      cost: "/skydial-bonus.svg",
    };

    return (
      <>
        <SectionHeading>PBX Solutions</SectionHeading>
        <div className="grid grid-cols-2 tablet:grid-cols-1 gap-6 mb-20">
          {solution.features.map((feature, idx) => (
            <TitledCard key={idx} feature={feature} path="features" color="text-textBlue" />
          ))}
        </div>

        <div className="mb-10 max-w-3xl">
          <span className="kicker mb-4">{t("solutions.skydial.benefits.label")}</span>
          <h2 className="text-3xl tablet:text-2xl font-semibold tracking-[-0.02em] text-black">
            {t("solutions.skydial.benefits.title")}
          </h2>
          <p className="mt-3 text-sm text-gray-600 leading-relaxed">
            {t("solutions.skydial.benefits.description")}
          </p>
        </div>
        <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-2 gap-6 mb-20">
          {Object.keys(benefitIcons).map((key) => (
            <div
              key={key}
              className="bg-white p-6 rounded-xl border border-line transition-[border-color,box-shadow,transform] duration-300 hover:shadow-lift hover:-translate-y-1 flex flex-col items-center text-center"
            >
              <img src={benefitIcons[key]} alt="" width="48" height="48" className="w-12 h-12 mb-3" loading="lazy" />
              <p className="text-base font-medium text-black">
                {t(`solutions.skydial.benefits.items.${key}`)}
              </p>
            </div>
          ))}
        </div>

        <SectionHeading>{t("solutions.skydial.keyfeatures.title")}</SectionHeading>
        <div className="grid grid-cols-3 tablet:grid-cols-2 mobile:grid-cols-1 gap-6 mb-20 justify-items-center">
          {Object.keys(featureIconsMap).map((key) => (
            <div
              key={key}
              className="bg-white p-6 rounded-xl border border-line w-full max-w-xs flex flex-col items-center gap-4 transition-[border-color,box-shadow,transform] duration-300 hover:shadow-lift hover:-translate-y-1 hover:border-textBlue/40"
            >
              <img src={featureIconsMap[key]} alt="" width="64" height="64" className="w-16 h-16" loading="lazy" />
              <p className="text-base font-medium text-black text-center">
                {t(`solutions.skydial.keyfeatures.items.${key}`)}
              </p>
            </div>
          ))}
        </div>

        <SectionHeading>{t("solutions.skydial.why.title")}</SectionHeading>
        <div className="grid grid-cols-2 tablet:grid-cols-1 gap-12 items-center">
          <div className="grid grid-cols-1 gap-6">
            {Object.keys(whyIcons).map((key) => (
              <div
                key={key}
                className="bg-white p-5 rounded-xl border border-line transition-[border-color,box-shadow,transform] duration-300 hover:shadow-lift hover:-translate-y-1 group"
              >
                <div className="flex items-center gap-4">
                  <span className="transition-transform duration-300 group-hover:scale-110 shrink-0">
                    <img src={whyIcons[key]} alt="" width="48" height="48" className="w-12 h-12" loading="lazy" />
                  </span>
                  <h3 className="text-base font-semibold text-textBlueDark transition-colors duration-300 group-hover:text-textBlue">
                    {t(`solutions.skydial.why.items.${key}`)}
                  </h3>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mobile:hidden">
            <img
              src="/img-skydial-why.svg"
              alt="Why Choose Skydial"
              width="480"
              height="360"
              className="max-w-full h-auto transition-transform duration-500 hover:scale-105"
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <section className="py-24 tablet:py-20 px-6 mobile:px-4 bg-surface hairline">
      <div className="container-page">
        {solution.isSkydial ? (
          renderSkydialFeatures()
        ) : solution.isSOC ? (
          renderSOCFeatures()
        ) : solution.isVAPT ? (
          renderVAPTFeatures()
        ) : solution.isFirstProtect ? (
          renderFirstProtectFeatures()
        ) : solution.hasCategories ? (
          solution.hasSectors ? (
            renderVMSFeatures()
          ) : (
            renderAICountingFeatures()
          )
        ) : (
          renderStandardFeatures()
        )}
      </div>
    </section>
  );
};

export default SolutionFeatures;
